import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { format } from "date-fns/esm";
import PropTypes from "prop-types";
import { getCountryCallingCode } from "libphonenumber-js";

// Lodash
import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import omit from "lodash-es/omit";

// Formik
import * as Yup from "yup";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

import validatePhoneNumberWithCountry from "src/utils/validatePhoneNumber";

// Icons
import send from "../../assets/icons/send-black.svg";
import intelIcon from "../../assets/icons/kulpa-i-black.svg";
import incognitoIcon from "../../assets/icons/user-kulpa-i-big.svg";

// Config
import { contactPreferences } from "../../config/values";
import { getRoute, urls } from "../../config/routes";
import { snackbarTypes } from "../../config/snackbar";
import customEvents from "../../config/customEvents";
import styles from "../../config/styles";

// Utils
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import incidents, { fromIncidentToForm } from "../../utils/incidents";

// Hooks
import { useFeatureFlags } from "../../context/FeatureFlagsProvider";
import useTranslate from "../../hooks/useTranslate";

// Actions
import { getCaseDetail, submitCase } from "../../state/caseDetail/actions";
import { toggleSnack } from "../../state/snackbar/actions";
import { updateCaseIncident } from "../../state/incident/actions";

// Components
import FormikHandler from "../../components/FormikHandler";
import LoadingSection from "../../components/LoadingSection";

// Handler
import CaseSubmitFormHandler from "./CaseSubmitFormHandler";
import ConfirmationDialog from "../../components/ConfirmationDialog/ConfirmationDialog";
import featureFlagKeys from "../../config/featureFlagKeys";
import useIncidentTypes from "src/hooks/useIncidentTypes";

const useStyles = makeStyles({
  root: {
    border: styles.values.border
  },
  submitButton: {
    marginRight: "auto",
    minWidth: 300
  },
  intelIcon: {
    transform: "translateY(2px)"
  }
});

// Contact Form Input Names
const startTimeName = contactPreferences.startTime.name;
const endTimeName = contactPreferences.endTime.name;
const channelName = contactPreferences.channel.name;
const notesName = contactPreferences.notes.name;
const emailName = contactPreferences.email.name;
const phoneNumberName = contactPreferences.phoneNumber.name;

const validationSchema = (texts, displayContact) => {
  const validationData = {
    description: Yup.string().required(texts.required),
    location: Yup.string().required(texts.required)
  };

  if (displayContact) {
    validationData.contact = Yup.object().shape({
      [startTimeName]: Yup.string(),
      [endTimeName]: Yup.string(),
      [channelName]: Yup.string(),
      [notesName]: Yup.string(),
      [emailName]: Yup.string().when(channelName, {
        is: contactPreferences.channel.values.EMAIL,
        then: schema =>
          schema.email(texts.invalidEmail).required(texts.required),
        otherwise: schema => schema
      }),
      [phoneNumberName]: Yup.string().when(channelName, {
        is: contactPreferences.channel.values.PHONE,
        then: schema =>
          schema
            .matches(/^[0-9]*$/, { message: texts.phoneNumber.mustBe })
            .min(4, texts.phoneNumber.tooShort)
            .test(
              "valid-phone",
              texts.phoneNumber.invalid,
              function validatePhoneNumber(value) {
                return validatePhoneNumberWithCountry(
                  value,
                  this.parent.country
                );
              }
            )
            .required(texts.required),
        otherwise: schema => schema
      })
    });
  }

  return Yup.object().shape(validationData);
};

const CaseIncidentsSubmit = ({ recipient }) => {
  const { caseId } = useParams();
  const { featureFlags } = useFeatureFlags();
  const { trackException, trackEvent } = useAppInsights();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  // Selectors
  const { data, isLoading, isSubmitting } = useSelector(s => s.caseDetail);
  const { isLoading: isLoadingIncident } = useSelector(s => s.caseIncident);
  const { userDetails } = useSelector(s => s.user);
  const incident = get(data, "incident") || {};
  const incidentTypes = useIncidentTypes();

  // State
  const [coord, setCoord] = useState(null);
  const [dateTimeError, setDateTimeError] = useState("");
  const [formValues, setFormValues] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);

  // Texts
  const getText = useTranslate();
  const schemaTexts = getText("common.validationSchema");
  const maxDateError = getText("common.validationSchema.examinationDateMax", {
    1: format(new Date(data.creationDateTime), "dd/MM/yy HH:mm")
  });
  const prompts = getText("common.prompts");

  // Flags
  const isCreator = data.isOwnedByUser;
  useEffect(() => {
    if (!initialized && !isEmpty(incident)) {
      setInitialized(true);
      setCoord(get(incident, "location.coordinate"));
    }
  }, [incident, initialized]);

  const crimeRefNumber = useMemo(() => {
    if (data.submissions && data.submissions.length > 0) {
      const submission = data.submissions.find(
        i => i.organization?.id === recipient.id
      );
      if (submission) return submission.referenceNumber;
    }
    return "";
  }, [data.submissions, recipient.id]);

  const incidentType = useMemo(() => {
    return incidentTypes.data.find(item => item.id === incident.incidentTypeId);
  }, [incident.incidentTypeId, incidentTypes]);

  if (isLoading || incidentTypes.isLoading || !incidentTypes.data) {
    return (
      <Paper elevation={0} className={classes.root}>
        <LoadingSection normalHeight />
      </Paper>
    );
  }

  const initialValues = fromIncidentToForm(incident, true, isCreator);
  const displayContact =
    featureFlags[featureFlagKeys.SUBMIT_CONTACT] && !data.isIntel;
  const location = get(incident, "location.name", "");

  const omitIncidents =
    initialValues.descriptions.length > 0 &&
    initialValues.dateTime !== null &&
    !!location;

  const onSubmit = (values, { setSubmitting, setFieldError }) => {
    let error = false;

    if (!coord) {
      setFieldError("location", schemaTexts.invalidSelect);
      error = true;
    }

    if (!values.incidentTypeId) {
      setFieldError("incidentTypeId", schemaTexts.invalidSelect);
      error = true;
    }

    if (!values.dateTime) {
      setDateTimeError(schemaTexts.required);
      error = true;
    }

    const maxDate = new Date(data.creationDateTime);
    if (values.dateTime > maxDate) {
      setDateTimeError(maxDateError);
      error = true;
    }

    setSubmitting(false);

    if (error) return;
    setFormValues(values);
    setShowSubmit(true);
  };

  const handleSubmit = () => {
    setShowSubmit(false);

    const showError = () => {
      dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
    };

    const onSuccess = () => {
      history.push(getRoute(urls.shareWith, { caseId }));
      // isUpdate: true keeps data/isLoading untouched while this resolves -
      // history.push above lands on the same route (no recipient in the
      // URL), so this component doesn't remount and keeps reading
      // data.creationDateTime/data.isOwnedByUser above the loading guard
      // (lines 146-152) - without isUpdate, data goes null the instant this
      // dispatches, and that read throws mid-render with no error boundary
      // to recover.
      dispatch(
        getCaseDetail({
          caseId,
          isUpdate: true,
          onFail: () => {
            showError();
            trackException(`Fail to get details from case: ${caseId}`);
          }
        })
      );
    };

    const {
      contact: { country, ...contact },
      ...incidentValues
    } = formValues;

    const onFinish = success => {
      if (success) {
        trackEvent(customEvents.SUBMIT_CASE);
        let body = {};

        if (displayContact) {
          if (
            contact[channelName] === contactPreferences.channel.values.PHONE
          ) {
            body = omit(contact, emailName);
            body[phoneNumberName] = `+${getCountryCallingCode(country)}${
              contact[phoneNumberName]
            }`;
          } else {
            body = omit(contact, phoneNumberName);
          }
        }

        if (data.crimeReferenceNumber) {
          // Case has reference number created by the police
          // Users cannot override
          // Validation also present on backend
          body.crimeReferenceNumber = data.crimeReferenceNumber;
        } else if (incidentValues.isReported) {
          // Crime reference number is set by user
          body.crimeReferenceNumber = incidentValues.crimeReferenceNumber;
        } else {
          // Crime is not reported
          // Remove ref number if added by user during case creation
          body.crimeReferenceNumber = null;
        }

        body.organizationId = recipient.id;

        dispatch(
          submitCase({
            caseId,
            onSuccess,
            body,
            onFail: () => {
              showError();
              trackException(`Failed to submit case: ${caseId}`);
            }
          })
        );
      } else showError();
    };

    const incidentData = {
      caseId,
      incident: {
        ...incidents({
          coord,
          incident,
          isDraft: true,
          values: incidentValues,
          isCreator
        }),
        descriptions: [incidentValues.description]
      },
      isDraft: true,
      author: {
        ...userDetails,
        profilePictureURL: userDetails.profilePhotoURL
      },
      onFinish
    };

    if (omitIncidents) onFinish(true);
    else dispatch(updateCaseIncident(incidentData));
  };

  const handleClose = () => {
    setShowSubmit(false);
    setFormValues(null);
  };

  const defaultContactValues = {
    contact: {
      [channelName]: contactPreferences.channel.values.EMAIL,
      [startTimeName]: "09:00",
      [endTimeName]: "17:00",
      [notesName]: "",
      [emailName]: userDetails.emailAddress || "",
      [phoneNumberName]: userDetails.phoneNumber?.nationalNumber || "",
      country: userDetails.phoneNumber?.isoCountryCode || "GB"
    }
  };

  const dialogProps = data.isIntel
    ? {
        isIntel: true,
        icon: incognitoIcon,
        title: getText("caseDetailPage.intelligenceSubmit.title"),
        texts: [
          {
            key: 1,
            text: getText("caseDetailPage.intelligenceSubmit.body", {
              isComponent: true,
              1: (
                <img
                  src={intelIcon}
                  className={classes.intelIcon}
                  alt="kulpa i"
                />
              )
            })
          }
        ]
      }
    : {
        icon: send,
        title: getText("caseDetailPage.submit.title"),
        texts: [{ key: 1, text: getText("caseDetailPage.submit.body") }]
      };

  return (
    <>
      <FormikHandler
        formik={{
          initialValues: {
            ...defaultContactValues,
            ...initialValues,
            description: initialValues.descriptions[0],
            location,
            isReported: !!crimeRefNumber,
            crimeReferenceNumber: crimeRefNumber,
            incidentTypeId: incidentType
          },
          onSubmit,
          validateOnBlur: false,
          validateOnChange: false,
          validationSchema: validationSchema(schemaTexts, displayContact)
        }}
        render={formikProps => (
          <CaseSubmitFormHandler
            {...formikProps}
            omitIncidents={omitIncidents}
            displayContact={displayContact}
            coord={coord}
            dateTimeError={dateTimeError}
            setCoord={setCoord}
            setDateTimeError={setDateTimeError}
            maxDate={data.creationDateTime}
            isReportedByPolice={!!crimeRefNumber}
          />
        )}
      />
      <ConfirmationDialog
        cancel={prompts.cancel}
        isLoading={Boolean(isLoadingIncident || isSubmitting)}
        onClose={handleClose}
        onConfirm={handleSubmit}
        open={showSubmit}
        type="submit"
        button={getText("caseDetailPage.submit.button")}
        large
        {...dialogProps}
      />
    </>
  );
};

CaseIncidentsSubmit.propTypes = {
  recipient: PropTypes.shape({
    id: PropTypes.string
  }).isRequired
};

export default CaseIncidentsSubmit;
